import { Component, SyntheticEvent } from 'react';
import cx from 'classnames';
import MediaQuery from 'react-responsive';
import { Col, ListGroupItem, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import ParentStatus from './ParentStatus/ParentStatus';
import { isParentsEmailSet } from './UtilityFunctions';
import styles from '../SCSS/StudentListItem.module.scss';
import { isUserHasKey } from '../GlobalFunctions';
import Tooltip from '../components/Tooltip';
import checkmarkInGrey from '../images/pkp-checkmark-in-circle-grey.svg';
import checkmarkInBlue from '../images/pkp-checkmark-in-circle-blue.svg';
import redXSymbol from '../images/pkp-x-symbol-red.svg';
import pencilGraphic from '../images/pkp-pencil.svg';
import garbageCanGraphic from '../images/pkp-garbage-can.svg';
import { Icon } from '@iconify/react';
import restart from '@iconify-icons/carbon/restart';
import { StudentProxyType } from '../../peekapak-types/DataProtocolTypes';

type Props = {
  isMiddleSchool: boolean;
  student: StudentProxyType;
  userId: string;
  onEditStudent: (userId: string, event: SyntheticEvent) => void;
  onDeleteStudent: (userId: string, event: SyntheticEvent) => void;
  onResetPeekaville: (userId: string, event: SyntheticEvent) => void;
  itemIndex: number;
  onClick?: (event: SyntheticEvent) => void;
};

type State = {
  isHover: boolean;
};

export class StudentListItem extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isHover: false,
    };

    this.handleOnDelete = this.handleOnDelete.bind(this);
    this.handleOnMouseLeave = this.handleOnMouseLeave.bind(this);
    this.handleOnMouseEnter = this.handleOnMouseEnter.bind(this);
    this.handleOnEdit = this.handleOnEdit.bind(this);
    this.handleOnResetPeekaville = this.handleOnResetPeekaville.bind(this);
  }

  handleOnDelete(event: SyntheticEvent<Element, Event>) {
    this.props.onDeleteStudent(this.props.userId, event);
  }

  handleOnEdit(event: SyntheticEvent<Element, Event>) {
    this.props.onEditStudent(this.props.userId, event);
  }

  handleOnResetPeekaville(event: SyntheticEvent<Element, Event>) {
    this.props.onResetPeekaville(this.props.userId, event);
  }

  handleOnMouseEnter() {
    this.setState({
      isHover: true,
    });
  }

  handleOnMouseLeave() {
    this.setState({
      isHover: false,
    });
  }

  render() {
    const loginPopover = (
      <Popover id='login-popover' className={styles.popover}>
        <strong>Username</strong>&nbsp;for&nbsp;
        {this.props.isMiddleSchool ? 'student' : 'myPeekaville'}&nbsp;login.
      </Popover>
    );

    const passwordPopover = (
      <Popover id='password-popover' className={styles.popover}>
        <strong>Password</strong>&nbsp;for&nbsp;
        {this.props.isMiddleSchool ? 'student' : 'myPeekaville'}&nbsp;login.
      </Popover>
    );

    const StatusIcon = () => {
      if (isParentsEmailSet(this.props.student)) {
        const invitationStatus = this.props.student.listOfParentProxies[0].invitationStatus;
        if (invitationStatus !== undefined) {
          switch (invitationStatus.status) {
            case 'Sent':
            case 'Opened':
              return <img alt='Circle' src={checkmarkInGrey} style={{ width: '18px', height: '18px' }} />;
            case 'Accepted':
              return <img alt='Checkmark' src={checkmarkInBlue} style={{ width: '18px', height: '18px' }} />;
            case 'Declined':
            case 'OptedOut':
            default:
              return <img alt='X symbol' src={redXSymbol} style={{ width: '16px', height: '16px' }} />;
          }
        }
      }

      return null;
    };

    return (
      <ListGroupItem
        className={cx(styles.StudentListItem, {
          [`${styles['top-item']}`]: this.props.itemIndex === 0,
        })}
        key={this.props.itemIndex}
        onClick={this.props.isMiddleSchool ? this.handleOnEdit : this.props.onClick}
        onMouseEnter={this.handleOnMouseEnter}
        onMouseLeave={this.handleOnMouseLeave}
      >
        <Row>
          <Col className='vcenter' md={3} xs={12}>
            <div className={styles.name}>
              {`${this.props.student.firstName} ${this.props.student.middleName ? this.props.student.middleName : ''} ${
                this.props.student.lastName ? this.props.student.lastName : ''
              }`}
            </div>
            <div className={styles.username}>
              {this.props.student.login && isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') && (
                <>
                  <MediaQuery minWidth={1750}>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='left' overlay={loginPopover}>
                      <div className={styles.login}>{this.props.student.login}</div>
                    </OverlayTrigger>
                  </MediaQuery>
                  <MediaQuery maxWidth={1749}>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='right' overlay={loginPopover}>
                      <div className={styles.login}>{this.props.student.login}</div>
                    </OverlayTrigger>
                  </MediaQuery>
                </>
              )}
            </div>
            <div className={styles.password}>
              {this.props.student.password && isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') && (
                <>
                  <MediaQuery minWidth={1750}>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='left' overlay={passwordPopover}>
                      <div className={styles.login}>{this.props.student.password}</div>
                    </OverlayTrigger>
                  </MediaQuery>
                  <MediaQuery maxWidth={1749}>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='right' overlay={passwordPopover}>
                      <div className={styles.login}>{this.props.student.password}</div>
                    </OverlayTrigger>
                  </MediaQuery>
                </>
              )}
            </div>
          </Col>
          <Col className='vcenter' md={7} xs={12}>
            {!this.props.isMiddleSchool && (
              <div className={styles.parentStatusContainer}>
                <ParentStatus parentsList={this.props.student.listOfParentProxies} student={this.props.student} />
              </div>
            )}
          </Col>
          <Col className='text-right vcenter' md={2} xs={12}>
            {this.state.isHover ? (
              <div className={styles.iconRow}>
                {isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') && !this.props.isMiddleSchool && (
                  <div
                    onClick={this.handleOnResetPeekaville}
                    style={{
                      cursor: 'pointer',
                      transform: 'translateY(-2px)',
                    }}
                  >
                    <Tooltip content={`Reset Student's Peekaville Quests`} direction='top'>
                      <Icon icon={restart} width={20} color='#86939e' />
                    </Tooltip>
                  </div>
                )}
                <img
                  alt='Pencil'
                  onClick={this.handleOnEdit}
                  src={pencilGraphic}
                  style={{
                    cursor: 'pointer',
                    height: '18px',
                  }}
                />
                <img
                  alt='Garbage can'
                  onClick={this.handleOnDelete}
                  src={garbageCanGraphic}
                  style={{ cursor: 'pointer', width: '17px' }}
                />
              </div>
            ) : (
              <div className='vcenter'>
                <StatusIcon />
              </div>
            )}
          </Col>
        </Row>
      </ListGroupItem>
    );
  }
}
